import type {
  AgentEnvironment,
  AgentMissionHistoryEntry,
  AgentMissionSource,
  AgentMissionStatus,
  AgentPolicyAssignment,
  AgentPurpose,
  DraftPolicy,
  EstateAgentActivity,
} from "@/api/temp/AgentEstateService";
import {
  hasOptionalStrings,
  hasStrings,
  isCount,
  isEntries,
  isList,
  isRecord,
  isString,
  isTimestamp,
} from "@/libs/agentSession";
import type { DeploymentRun, NativeOverride } from "./deployment";
import type { McpConnectivityState } from "./mcpConnectivity";
import { PLATFORM_LABELS, PURPOSE_LABELS } from "./seed";

/** Administrator decisions stay separate from the generated discovery record. */
export interface AgentMissionDecision {
  status: AgentMissionStatus;
  source: AgentMissionSource;
  name?: string;
  purposeCategory?: AgentPurpose;
  description?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  lastReviewedAt?: string;
  contextResolved: boolean;
  history: AgentMissionHistoryEntry[];
}

/** One MCP service an administrator connected to an agent. */
export interface McpBinding {
  serverId: string;
  at: string;
  by: string;
}

export interface AgentEstateSnapshot {
  environments: AgentEnvironment[];
  assignments: [string, AgentPolicyAssignment[]][];
  activity: [string, EstateAgentActivity[]][];
  appliedAt: [string, string][];
  revokedAgentIds: string[];
  missionDecisions: [string, AgentMissionDecision][];
  draftPolicies: [string, DraftPolicy][];
  /** Optional so sessions saved before deployment tracking still load. */
  deploymentRuns?: [string, DeploymentRun[]][];
  nativeOverrides?: [string, NativeOverride][];
  /** Optional so sessions saved before agent selection still load. */
  importedAgentIds?: [string, string[]][];
  mcpAuthenticationState?: [string, McpConnectivityState][];
  /** Optional so sessions saved before manual MCP connections still load. */
  mcpBindings?: [string, McpBinding[]][];
}

const isEnvironment = (value: unknown) =>
  isRecord(value) &&
  hasStrings(value, ["id", "name", "platform"]) &&
  Object.hasOwn(PLATFORM_LABELS, value.platform as string) &&
  ["PRODUCTION", "TEST", "DEVELOPMENT"].includes(value.environmentType as string) &&
  ["MICROSOFT_ENTRA_ID", "OKTA", "AWS_IAM", "OTHER", "CONFIGURE_LATER"].includes(value.identityProvider as string) &&
  ["HEALTHY", "WARNING", "DISABLED"].includes(value.health as string) &&
  hasOptionalStrings(value, ["businessContext"]) &&
  isTimestamp(value.startedAt) &&
  isCount(value.totalAgents);

const isAssignment = (value: unknown) =>
  isRecord(value) && hasStrings(value, ["id", "name"]) && isTimestamp(value.appliedAt);

const isActivity = (value: unknown) =>
  isRecord(value) &&
  hasStrings(value, ["id", "action", "service", "policyName"]) &&
  isTimestamp(value.occurredAt) &&
  ["ALLOWED", "DENIED", "WARNING", "RECORDED"].includes(value.decision as string) &&
  (value.kind === undefined || ["RUNTIME", "POLICY_APPLIED", "ACCESS_SUSPENDED", "ACCESS_RESTORED"].includes(value.kind as string)) &&
  (value.governanceViolation === undefined || typeof value.governanceViolation === "boolean") &&
  hasOptionalStrings(value, ["policyId", "requestId", "enforcementPoint"]);

const isMissionDecision = (value: unknown) =>
  isRecord(value) &&
  ["ADMINISTRATOR_DEFINED", "INFERRED_NEEDS_REVIEW", "CONFIRMED", "OVERRIDDEN", "NEEDS_MORE_CONTEXT", "REVIEW_RECOMMENDED"].includes(value.status as string) &&
  ["ADMINISTRATOR", "IMPORTED", "TRUSTAI_INFERRED", "ADMINISTRATOR_OVERRIDE"].includes(value.source as string) &&
  hasOptionalStrings(value, ["name", "description", "confirmedAt", "confirmedBy", "lastReviewedAt"]) &&
  (value.purposeCategory === undefined || (isString(value.purposeCategory) && Object.hasOwn(PURPOSE_LABELS, value.purposeCategory))) &&
  typeof value.contextResolved === "boolean" &&
  isList(value.history, item =>
    isRecord(item) && hasStrings(item, ["id", "action", "detail", "actor"]) && isTimestamp(item.occurredAt),
  );

const isDraft = (value: unknown) =>
  isRecord(value) &&
  hasStrings(value, ["id", "intent", "intentSummary", "suggestedName", "category", "technology"]) &&
  isList(value.unmatchedTerms, isString) &&
  isList(value.controls, item =>
    isRecord(item) && hasStrings(item, ["label", "derivedFrom"]) && ["ALLOW", "BLOCK"].includes(item.sense as string),
  );

const isDelay = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isRun = (value: unknown) =>
  isRecord(value) &&
  hasStrings(value, ["id"]) &&
  ["APPLY", "REAPPLY", "RETRY"].includes(value.kind as string) &&
  isTimestamp(value.startedAt) &&
  isRecord(value.timing) &&
  ["createDelayMs", "deployDelayMs", "verifyDelayMs", "platformLagMs"].every(key =>
    isDelay((value.timing as Record<string, unknown>)[key]),
  ) &&
  isList(value.childIds, isString) &&
  isList(value.createdIds, isString) &&
  isList(value.failingIds, isString);

const isOverride = (value: unknown) =>
  isRecord(value) &&
  isTimestamp(value.modifiedAt) &&
  isRecord(value.current) &&
  hasStrings(value.current, ["subject", "rule"]) &&
  ["ALLOW", "BLOCK", "MASK", "REQUIRE_APPROVAL"].includes(value.current.effect as string);

const SECRET_FIELDS = ["clientSecret", "privateKey", "passphrase"];

/** A stored connection profile - and never one holding a secret. */
const isProfileState = (value: unknown) =>
  isRecord(value) &&
  typeof value.version === "number" &&
  typeof value.credentialVersion === "number" &&
  typeof value.configured === "boolean" &&
  isRecord(value.formValues) &&
  !SECRET_FIELDS.some(key => {
    const authConfig = (value.formValues as Record<string, unknown>)
      .authConfig;
    return isRecord(authConfig) && key in authConfig;
  }) &&
  Array.isArray(value.evidence) &&
  Array.isArray(value.jobs);

const isBinding = (value: unknown) =>
  isRecord(value) &&
  hasStrings(value, ["serverId", "by"]) &&
  isTimestamp(value.at);

const isConnectivityState = (value: unknown) =>
  isRecord(value) && Object.values(value).every(isProfileState);

export const isAgentEstateSnapshot = (value: unknown): value is AgentEstateSnapshot =>
  isRecord(value) &&
  isList(value.environments, isEnvironment) &&
  isEntries(value.assignments, item => isList(item, isAssignment)) &&
  isEntries(value.activity, item => isList(item, isActivity)) &&
  isEntries(value.appliedAt, isTimestamp) &&
  isList(value.revokedAgentIds, isString) &&
  isEntries(value.missionDecisions, isMissionDecision) &&
  isEntries(value.draftPolicies, isDraft) &&
  (value.deploymentRuns === undefined ||
    isEntries(value.deploymentRuns, item => isList(item, isRun))) &&
  (value.nativeOverrides === undefined || isEntries(value.nativeOverrides, isOverride)) &&
  (value.importedAgentIds === undefined ||
    isEntries(value.importedAgentIds, item => isList(item, isString))) &&
  (value.mcpAuthenticationState === undefined ||
    isEntries(value.mcpAuthenticationState, isConnectivityState)) &&
  (value.mcpBindings === undefined ||
    isEntries(value.mcpBindings, item => isList(item, isBinding)));
